import { css } from '@styled-system/css';
import { type FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import amplitude from '@/amplitude';
import { PriceDistributionCard } from '@/features/analysisPageStatistics/components/PriceDistributionCard';
import type { ProductItemCondition } from '@/shared/api/types/generated';
import { Dropdown, type DropdownOption } from '@/shared/components/ui/Dropdown';
import { Message } from '@/shared/components/ui/Message';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { getAvailableItemConditions } from '@/shared/utils/analysis/itemCondition';
import { formatNumber, formatPercentage, formatPrice, formatTime } from '@/shared/utils/format';

import { useAnalysisStatistics } from '../hooks';
import type { AnalysisStatisticsData } from '../types';
import { DoubleStatCard } from './DoubleStatCard';
import { PriceDistributionCurve } from './PriceDistributionCurve';
import { StatCard } from './StatCard';

interface AnalysisPageStatisticsContentProps {
  data: AnalysisStatisticsData | undefined;
  loading: boolean;
}

const AnalysisPageStatisticsContent: FC<AnalysisPageStatisticsContentProps> = ({ data, loading }) => {
  const isMobile = useIsMobile();

  const containerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'lg',
  });

  const soloStatsStyles = css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '2.1875rem', // 35px
    alignItems: 'center',
    justifyContent: isMobile ? 'center' : 'flex-start',
  });

  const doubleStatsStyles = css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '2.1875rem', // 35px
    alignItems: 'center',
    justifyContent: isMobile ? 'center' : 'flex-start',
  });

  const distributionSectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.625rem', // 10px
    w: '100%',
  });

  const distributionTitleStyles = css({
    fontSize: 'sm', // 11px
    fontWeight: 'medium',
    fontFamily: 'Manrope, sans-serif',
    color: 'font.primary', // #e6e6e9
    lineHeight: '1.07',
  });

  return (
    <div className={containerStyles}>
      <div className={soloStatsStyles}>
        <StatCard
          value={formatPrice(data?.averageSalePrice ?? null)}
          label="Prix moyen de vente"
          icon="💰"
          loading={loading}
        />
        <StatCard
          value={formatPercentage(data?.salePercentage ?? null)}
          label="Produits ajoutés qui sont vendus"
          icon="⚡️"
          loading={loading}
        />
        <StatCard
          value={formatTime(data?.averageSaleTimeSec ?? null, 'min')}
          label="Temps moyen de vente"
          icon="🕒"
          loading={loading}
        />
      </div>

      <div className={doubleStatsStyles}>
        <DoubleStatCard
          title="Nombre d'analyses"
          stat1={{
            value: formatNumber(data?.totalProductsAnalyzed ?? null),
            label: 'Nombre total de produits dans l’analyse',
            icon: '📍',
            isHighlight: true,
          }}
          stat2={{
            value: formatNumber(data?.newProductsPerDay ?? null),
            label: 'Nombre de nouveaux produits ajoutés par jour',
            icon: '🆕',
          }}
          loading={loading}
        />
        <DoubleStatCard
          title="Nombre de ventes"
          stat1={{
            value: formatNumber(data?.totalSalesRecorded ?? null),
            label: 'Nombre total de ventes enregistrées',
            icon: '💸',
            isHighlight: true,
          }}
          stat2={{
            value: formatNumber(data?.salesPerDay ?? null),
            label: 'Nombre de ventes par jour',
            icon: '🛍️',
          }}
          loading={loading}
        />
      </div>

      <div className={distributionSectionStyles}>
        <div className={distributionTitleStyles}>Répartition des ventes</div>
        <PriceDistributionCard
          q1Price={data?.priceDistributionQ1 ?? null}
          q3Price={data?.priceDistributionQ3 ?? null}
          loading={loading}
        />
        {!isMobile && (
          <PriceDistributionCurve
            buckets={data?.priceDistributionBuckets ?? null}
            priceMin={data?.priceDistributionMin ?? null}
            priceMax={data?.priceDistributionMax ?? null}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export const AnalysisPageStatistics: FC = () => {
  const { id: analysisId } = useParams<{ id: string }>();

  // État sélectionné (par défaut "Très bon état" pendant le loading)
  const [selectedCondition, setSelectedCondition] = useState<ProductItemCondition | null>(null);

  // Récupérer les données statistiques
  const { data, loading, error, availableConditions } = useAnalysisStatistics({
    analysisId,
    itemCondition: selectedCondition,
  });

  // Options pour le dropdown basées sur les conditions disponibles
  const itemConditionOptions = useMemo(() => {
    const conditionOptions = getAvailableItemConditions().filter((option) =>
      availableConditions.includes(option.value as ProductItemCondition),
    );

    // Ajouter l'option "Tous les états" en première position
    return [{ value: 'all', label: 'Tous les états' }, ...conditionOptions];
  }, [availableConditions]);

  const handleConditionSelect = (option: DropdownOption) => {
    // Tracker l'événement avec Amplitude
    amplitude.track('TEST Statistiques Filter Etats', { itemCondition: option.value });

    setSelectedCondition(option.value === 'all' ? null : (option.value as ProductItemCondition));
  };

  const containerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  });

  const filterSectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'md',
  });

  return (
    <div className={containerStyles}>
      {data && (data?.totalSalesRecorded ?? 0) === 0 ? (
        <Message type="info" bordered className={css({ mb: 'sm' })}>
          Les statistiques sont mises à jour toutes les heures. Les analyses récentes peuvent ne pas encore afficher de
          données complètes.
        </Message>
      ) : null}
      <div className={filterSectionStyles}>
        <Dropdown
          options={itemConditionOptions}
          value={selectedCondition ?? 'all'}
          onSelect={handleConditionSelect}
          menuWidth="12rem"
          maxHeight="20rem"
        />

        {error ? (
          <Message type="error" bordered>
            {error}
          </Message>
        ) : (
          <AnalysisPageStatisticsContent data={data} loading={loading} />
        )}
      </div>
    </div>
  );
};
